import { ListeSkeleton } from '@/components/ui/Skeleton'
import { BarreDeRetour } from '@/components/ui/BarreDeRetour'
import { VisageEnSilence } from '@/features/feed/Visage'
import { RichText } from '@/lib/content/RichText'
import { porteDesFormules, useMath } from '@/lib/content/useMath'
import type { LeconEnLectureSeule } from '@/lib/api/parcours'

/**
 * Une partie de cours lue SANS que rien ne s'enregistre — RG-3.16,
 * [DECISIONS §37], §174.
 *
 * **Cet écran ne pose aucune coche, et il ne le peut pas.** Il ne connaît aucune
 * route qui écrive : la leçon lui arrive déjà chargée, par une route qui n'écrit
 * rien. Confier la règle à la discipline de l'écran — « ne pas appeler
 * `marquerVue` ici » — l'aurait perdue au premier écran suivant qui aurait
 * recopié le composant. C'est précisément ce qui a failli arriver quand la
 * banque d'exercices a voulu sa propre porte.
 *
 * **Pourquoi elle compte, cette règle.** La coche ouvre le quiz de section, puis
 * le chapitre, puis l'examen. La poser sur un passage consulté de biais, au
 * milieu du défilement, ouvrirait des portes sur du contenu jamais travaillé —
 * et l'examen final finirait par tomber sur quelqu'un qui n'a rien lu.
 *
 * **Deux portes, un seul écran** — §174. Le tag du Fil et l'exercice de la
 * banque mènent ici tous les deux. Deux composants jumeaux auraient fini par
 * dire deux choses différentes de la même garantie, et une phrase de garantie
 * qui dérive ne garantit plus rien. Seul le libellé du retour change : il nomme
 * l'endroit d'où l'on vient, qui n'est pas le même.
 *
 * **Le bouton retour reste, toujours.** C'est la seule sortie : cet écran
 * remplace ce qu'on regardait le temps de la lecture, et le candidat doit
 * retrouver sa place, pas rouvrir l'application.
 */
export function LectureSansCoche({
  lecon,
  isLoading,
  isError,
  retour,
  economieDeDonnees = false,
  onQuitter,
}: {
  lecon: LeconEnLectureSeule | undefined
  isLoading: boolean
  isError: boolean
  /** Où l'on retourne — « Retour au fil », « Retour à l'exercice ». */
  retour: string
  economieDeDonnees?: boolean
  onQuitter: () => void
}) {
  return (
    <section className="flex flex-col gap-4 px-4 pb-10">
      {/* RG-D22ter — pas de mascotte pendant une lecture : elle n'a rien à
          commenter, et un personnage qui parle sans rien à dire cesse d'être
          écouté quand il a quelque chose à dire. */}
      <VisageEnSilence />

      {/* Le retour est EN HAUT et collant : c'est là que l'œil cherche une
          sortie, et la bande d'en-tête est justement vide pendant une lecture,
          le visage s'y taisant. */}
      <BarreDeRetour
        libelle={retour}
        fil={lecon === undefined ? undefined : `${lecon.cours_titre} › ${lecon.chapitre_titre}`}
        onClick={onQuitter}
      />

      {isLoading && <ListeSkeleton annonce="Chargement de la leçon…" lignes={6} hauteur="1.5rem" />}

      {isError && (
        <p className="text-[15px]" style={{ color: 'var(--texte-2)' }}>
          Cette partie de cours n’est pas encore rédigée. Elle arrivera : le
          corpus se remplit chapitre par chapitre.
        </p>
      )}

      {lecon !== undefined && (
        <>
          {/* Le chemin de lecture est monté dans la barre collante : le
              répéter ici en ferait deux fois la même ligne à l'écran. */}
          <h2 className="text-[20px] font-bold">{lecon.titre}</h2>

          <Corps texte={lecon.content_md} economieDeDonnees={economieDeDonnees} />

          {/* Dit à la FIN et non au début : au début, c'est un avertissement
              qu'on n'a pas demandé ; à la fin, c'est la réponse à la question
              qu'on se pose en refermant — « est-ce que ça a compté ? ». */}
          <p
            className="rounded-2xl border p-3 text-[13px]"
            style={{ borderColor: 'var(--bordure)', color: 'var(--texte-2)' }}
          >
            Cette lecture ne compte pas comme une section terminée. Pour poser
            la coche et ouvrir le quiz, ouvrez cette section depuis le Parcours.
          </p>
        </>
      )}
    </section>
  )
}

/**
 * Le corps de la leçon.
 *
 * Séparé pour que `useMath` soit appelé inconditionnellement : il ne peut pas
 * l'être dans le composant parent, où le texte n'existe qu'après la requête.
 * KaTeX n'est chargé que si la leçon en a besoin (ADR-07).
 */
function Corps({
  texte,
  economieDeDonnees,
}: {
  texte: string
  economieDeDonnees: boolean
}) {
  // §153 — le critère du serveur, et non un `$` quelconque.
  const rendreMath = useMath(porteDesFormules(texte))

  // `avecTitres` : le contenu de leçon porte des `###` qui doivent devenir des
  // titres. Sans cette option, ils s'affichaient tels quels, dièses compris.
  return <RichText texte={texte} avecTitres options={{ economieDeDonnees, rendreMath }} />
}
